/**
 * Permission Service - Main Service Interface
 * 
 * This is the main service that components and other parts of the application
 * will interact with for permission checking. It handles caching, data fetching,
 * and provides convenient methods for common permission checks.
 */

import { invokeEdgeFunction } from '@/lib/supabase-functions-wrapper';
import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  ProjectMembership,
  CompanyContract,
  IPermissionService,
  UserRole,
  ProjectPermission,
  PermissionAction,
  PermissionResource
} from '../types';
import { PermissionEngine } from './permission-engine';
import { PermissionCache } from '../utils/permission-cache';
import { systemLogger } from '@/lib/logging';

/**
 * Main permission service implementation
 */
export class PermissionService implements IPermissionService {
  private engine: PermissionEngine;
  private cache: PermissionCache;

  constructor() {
    this.engine = new PermissionEngine();
    this.cache = new PermissionCache();
  }

  /**
   * Core permission checking method
   */
  async hasPermission(
    user: UserContext,
    requirement: PermissionRequirement
  ): Promise<PermissionResult> {
    // System Admin bypass: Skip cache for enhanced security
    if (this.isSystemAdmin(user)) {
      // PERFORMANCE: Fetch all permissions in single call (was 2 duplicate calls)
      const permissionsData = await this.fetchAllPermissions(
        user.id,
        user.company_id,
        requirement.resourceId
      );

      // Evaluate permission directly
      const result = await this.engine.evaluatePermission(
        user,
        requirement,
        permissionsData.projectMemberships,
        permissionsData.companyContracts
      );

      return result;
    }

    // Generate cache key
    const cacheKey = this.generateCacheKey(user, requirement);

    // Check cache first
    const cachedResult = this.cache.get(cacheKey);
    if (cachedResult) {
      return cachedResult.result;
    }

    // PERFORMANCE: Fetch all permissions in single call (was 2 duplicate calls)
    const permissionsData = await this.fetchAllPermissions(
      user.id,
      user.company_id,
      requirement.resourceId
    );

    // Evaluate permission
    const result = await this.engine.evaluatePermission(
      user,
      requirement,
      permissionsData.projectMemberships,
      permissionsData.companyContracts
    );

    // Cache the result
    this.cache.set(cacheKey, result);

    return result;
  }

  /**
   * PERFORMANCE: Evaluate permissions using pre-fetched data (from PermissionProvider cache)
   *
   * This method allows PermissionProvider to use its React Query cached data
   * instead of triggering new edge function calls via hasPermission()
   *
   * Architecture:
   * - PermissionProvider fetches permissions ONCE via useQuery
   * - Stores in React Query cache with 5-minute staleTime
   * - Calls this method with cached data for all permission checks
   * - Result: 1 fetch total, NOT 1 fetch per permission check
   *
   * @param user - User context
   * @param requirement - Permission requirement to evaluate
   * @param projectMemberships - Pre-fetched project memberships
   * @param companyContracts - Pre-fetched company contracts
   * @returns Permission evaluation result
   */
  async evaluateWithProvidedData(
    user: UserContext,
    requirement: PermissionRequirement,
    projectMemberships: ProjectMembership[],
    companyContracts: CompanyContract[]
  ): Promise<PermissionResult> {
    // Use the permission engine to evaluate with provided data
    // This bypasses fetching entirely - data is already cached
    const result = await this.engine.evaluatePermission(
      user,
      requirement,
      projectMemberships,
      companyContracts
    );

    return result;
  }

  // ============================================================================
  // COMPANY PERMISSIONS
  // ============================================================================

  async canViewCompany(user: UserContext, companyId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.VIEW,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }

  async canEditCompany(user: UserContext, companyId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }

  async canDeleteCompany(user: UserContext, companyId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.DELETE,
      resource: PermissionResource.COMPANY,
      resourceId: companyId
    });
    return result.allowed;
  }

  // ============================================================================
  // PROJECT PERMISSIONS
  // ============================================================================

  async canViewProject(user: UserContext, projectId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.VIEW,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  async canEditProject(user: UserContext, projectId: string, projectStatus?: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId,
      context: { projectStatus }
    });
    return result.allowed;
  }

  async canDeleteProject(user: UserContext, projectId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.DELETE,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  // ============================================================================
  // USER PERMISSIONS
  // ============================================================================

  async canCreateUser(user: UserContext, targetCompanyId?: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.CREATE,
      resource: PermissionResource.USER,
      ...(targetCompanyId !== undefined && { companyScope: targetCompanyId })
    });
    return result.allowed;
  }

  async canEditUser(user: UserContext, targetUserId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.USER,
      resourceId: targetUserId
    });
    return result.allowed;
  }

  async canDeleteUser(user: UserContext, targetUserId: string): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.DELETE,
      resource: PermissionResource.USER,
      resourceId: targetUserId
    });
    return result.allowed;
  }

  // ============================================================================
  // PROJECT-SPECIFIC OPERATIONS
  // ============================================================================

  async canCreateSubproject(user: UserContext, projectId: string, projectStatus?: string): Promise<boolean> {
    // Clear cache for this specific permission check to avoid stale results
    const cacheKey = this.generateCacheKey(user, {
      action: PermissionAction.CREATE,
      resource: PermissionResource.SUBPROJECT,
      resourceId: projectId,
      context: { projectStatus }
    });
    this.cache.clear(cacheKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // Escape regex special chars

    systemLogger.debug('[PermissionService] Checking canCreateSubproject', {
      userId: user.id,
      userRole: user.app_role,
      companyType: user.company_type,  // Now from JWT!
      projectId,
      projectStatus
    });

    // For all users, check specific subproject creation permission with project status context
    const result = await this.hasPermission(user, {
      action: PermissionAction.CREATE,
      resource: PermissionResource.SUBPROJECT,
      resourceId: projectId,
      context: { projectStatus }
    });

    systemLogger.debug('[PermissionService] canCreateSubproject result', {
      userId: user.id,
      projectId,
      allowed: result.allowed,
      reason: result.reason
    });

    return result.allowed;
  }

  async canCreateProjectReport(user: UserContext, projectId: string): Promise<boolean> {
    // System Admin can always create reports
    if (this.isSystemAdmin(user)) {
      return true;
    }

    // Check if user can create reports for this project
    const result = await this.hasPermission(user, {
      action: PermissionAction.CREATE,
      resource: PermissionResource.REPORT,
      resourceId: projectId
    });
    return result.allowed;
  }

  async canAcceptProject(user: UserContext, projectId: string): Promise<boolean> {
    // System Admin can always accept projects
    if (this.isSystemAdmin(user)) {
      return true;
    }

    // Get project details to check performing company and status
    const projectDetails = await this.getProjectDetails(projectId);
    if (!projectDetails) {
      return false;
    }

    // Check if project status allows acceptance (SENT or REJECTED)
    if (!['SENT', 'REJECTED'].includes(projectDetails.status)) {
      return false;
    }

    // Only performing company members can accept projects
    if (user.company_id !== projectDetails.performing_company_id) {
      return false;
    }

    // Check if user has edit permission on the project
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  async canRejectProject(user: UserContext, projectId: string): Promise<boolean> {
    // System Admin can always reject projects
    if (this.isSystemAdmin(user)) {
      return true;
    }

    // Get project details to check performing company and status
    const projectDetails = await this.getProjectDetails(projectId);
    if (!projectDetails) {
      return false;
    }

    // Check if project status allows rejection (SENT or REJECTED)
    if (!['SENT', 'REJECTED'].includes(projectDetails.status)) {
      return false;
    }

    // Only performing company members can reject projects
    if (user.company_id !== projectDetails.performing_company_id) {
      return false;
    }

    // Check if user has edit permission on the project
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  async canChangeProjectStatus(user: UserContext, projectId: string): Promise<boolean> {
    // Status changes are a form of project editing
    const result = await this.hasPermission(user, {
      action: PermissionAction.EDIT,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  // ============================================================================
  // PROJECT MEMBERSHIP MANAGEMENT
  // ============================================================================

  async canGrantProjectPermission(
    user: UserContext,
    projectId: string,
    _targetUserId: string,
    permissionLevel: ProjectPermission
  ): Promise<boolean> {
    // Check if user can assign permissions on this project
    const assignResult = await this.hasPermission(user, {
      action: PermissionAction.ASSIGN,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });

    if (!assignResult.allowed) return false;

    // Additional logic: Can't grant permissions higher than your own
    const userProjectPermission = await this.getUserProjectPermission(user, projectId);
    if (userProjectPermission) {
      const userHierarchy = this.getProjectPermissionHierarchy(userProjectPermission);
      const targetHierarchy = this.getProjectPermissionHierarchy(permissionLevel);

      // Can't grant permission higher than your own (except System Admin)
      if (!this.isSystemAdmin(user) && targetHierarchy > userHierarchy) {
        return false;
      }
    }

    return true;
  }

  async canRevokeProjectPermission(
    user: UserContext,
    projectId: string,
    _targetUserId: string
  ): Promise<boolean> {
    // Check if user can assign/manage permissions on this project
    const result = await this.hasPermission(user, {
      action: PermissionAction.ASSIGN,
      resource: PermissionResource.PROJECT,
      resourceId: projectId
    });
    return result.allowed;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  getUserRole(user: UserContext): UserRole {
    if (user.is_system_admin) return UserRole.ADMIN;
    if (user.app_role) return user.app_role;
    if (user.profile?.role) return user.profile.role;
    return UserRole.USER;
  }

  async getUserProjectPermission(user: UserContext, projectId: string): Promise<ProjectPermission | null> {
    // First check if they're a Company Admin (automatic Editor permissions)
    if (this.getUserRole(user) === UserRole.COMPANY_ADMIN) {
      return ProjectPermission.EDITOR;
    }

    // PERFORMANCE: Use optimized fetch method
    const permissionsData = await this.fetchAllPermissions(user.id, user.company_id, projectId);
    const membership = permissionsData.projectMemberships.find((m: ProjectMembership) => m.assignment_id === projectId);

    return membership ? membership.permission_level : null;
  }

  isSystemAdmin(user: UserContext): boolean {
    return user.is_system_admin === true || 
           user.app_role === UserRole.ADMIN ||
           user.profile?.role === UserRole.ADMIN;
  }

  isCompanyAdmin(user: UserContext): boolean {
    return user.app_role === UserRole.COMPANY_ADMIN ||
           user.profile?.role === UserRole.COMPANY_ADMIN;
  }

  // ============================================================================
  // DATA FETCHING METHODS
  // ============================================================================

  /**
   * PERFORMANCE FIX: Fetch all permissions data in a single call
   *
   * Before: fetchProjectMemberships() and fetchCompanyContracts() each called get-user-permissions
   * Result: 2 duplicate calls to same edge function per permission check
   * Evidence: Console logs showed PermissionService.fetchProjectMemberships in caller traces
   *
   * After: Single call returns both datasets
   * Impact: 24 calls → 12 calls (50% reduction)
   */
  private async fetchAllPermissions(
    _userId: string,
    companyId?: string,
    specificProjectId?: string
  ): Promise<{
    projectMemberships: ProjectMembership[];
    companyContracts: CompanyContract[];
  }> {
    try {
      // Single call to get both memberships and contracts
      const { data, error } = await invokeEdgeFunction('get-user-permissions');

      if (error) {
        systemLogger.error('[PermissionService] Failed to fetch permissions:', error);
        return { projectMemberships: [], companyContracts: [] };
      }

      const typedData = data as {
        project_memberships?: ProjectMembership[];
        company_contracts?: CompanyContract[];
      };

      // Extract project memberships
      let projectMemberships = typedData.project_memberships || [];

      // Filter by specific project if requested
      if (specificProjectId) {
        projectMemberships = projectMemberships.filter(
          (m: ProjectMembership) => m.assignment_id === specificProjectId
        );
      }

      // Extract company contracts (only if companyId provided)
      const companyContracts = companyId ? (typedData.company_contracts || []) : [];

      return { projectMemberships, companyContracts };
    } catch (_error) {
      systemLogger.error('[PermissionService] Failed to fetch permissions:', _error);
      return { projectMemberships: [], companyContracts: [] };
    }
  }

  // ============================================================================
  // PROJECT DATA FETCHING
  // ============================================================================

  /**
   * Get project details for permission checking
   */
  private async getProjectDetails(projectId: string): Promise<{
    id: string;
    status: string;
    performing_company_id: string | null;
    ordering_company_id: string | null;
  } | null> {
    try {
      // Use the invokeEdgeFunction to get project details
      const { invokeEdgeFunctionSafely } = await import('@/lib/supabase-functions-wrapper');

      const { data, error } = await invokeEdgeFunctionSafely('get-project-details', {
        body: { projectId }
      });

      if (error || !data) {
        return null;
      }

      // Type the data response properly
      const typedData = data as {
        id: string;
        status: string;
        performing_company_id: string | null;
        ordering_company_id: string | null;
      };

      return {
        id: typedData.id,
        status: typedData.status,
        performing_company_id: typedData.performing_company_id,
        ordering_company_id: typedData.ordering_company_id
      };
    } catch (_error) {
      return null;
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private generateCacheKey(user: UserContext, requirement: PermissionRequirement): string {
    const keyParts = [
      `user:${user.id}`,
      `role:${this.getUserRole(user)}`,
      `company:${user.company_id || 'none'}`,
      `action:${requirement.action}`,
      `resource:${requirement.resource}`,
      `resourceId:${requirement.resourceId || 'none'}`,
      `companyScope:${requirement.companyScope || 'none'}`
    ];
    
    return keyParts.join('|');
  }

  private getProjectPermissionHierarchy(permission: ProjectPermission): number {
    const hierarchy = {
      [ProjectPermission.OWNER]: 4,
      [ProjectPermission.EDITOR]: 3,
      [ProjectPermission.OPERATOR]: 2,
      [ProjectPermission.VIEWER]: 1
    };
    return hierarchy[permission] || 0;
  }

  /**
   * Check if user can create projects
   */
  async canCreateProject(user: UserContext): Promise<boolean> {
    const result = await this.hasPermission(user, {
      action: PermissionAction.CREATE,
      resource: PermissionResource.PROJECT
      // Note: No resourceId needed for project creation
    });
    return result.allowed;
  }

  /**
   * Clear cache for a specific user (useful when user permissions change)
   */
  clearUserCache(userId: string): void {
    this.cache.clearUser(userId);
  }

  /**
   * Clear all permission cache (useful for system-wide permission changes)
   */
  clearAllCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const permissionService = new PermissionService();